// Columna de lista de las tablas POS: píldoras que cuentan y filtran, barra con conteo y atajos, y
// la `DataTable` compartida (densidad regular, encabezados ordenables). Cada pantalla pone sus columnas.
import type { ReactNode } from 'react';
import { ErrorState } from '@/kit';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { StatsBar, type StatsBarItem } from '@/shared/patterns/StatsBar';
import { TableToolbar } from '@/shared/patterns/TableToolbar';
import { StatusPill } from '@/shared/patterns/Pills';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { productTierLabel } from '@/shared/lib/format/productTier';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { isService } from '@/domains/sales/wizard/catalogAvailability';
import { availableOf, promoLabelOf, type PosSort, type PosSortKey } from '../lib/posCatalog';
import { hasPromo, type PosStat, type PosStats } from '../lib/posFilters';

export const POS_KBD =
    'mx-0.5 rounded-[3px] border border-border bg-muted px-[5px] py-px text-[10px] font-semibold';

export type PosColumn = DataTableColumn<CatalogItem, PosSortKey>;

const DASH = '—';

/** Columnas comunes; `lowStock` pinta el stock en ámbar y `modelDetail` agrega capacidad o color. */
export function posColumns({
    isLowStock,
    modelDetail,
}: {
    isLowStock: (item: CatalogItem) => boolean;
    modelDetail?: (item: CatalogItem) => string;
}): Record<'sku' | 'brand' | 'model' | 'tier' | 'price' | 'stock' | 'promo', PosColumn> {
    const modelText = (item: CatalogItem): string =>
        [item.model, modelDetail?.(item) ?? ''].filter((part) => part !== '').join(' · ');
    return {
        sku: {
            key: 'sku',
            label: 'SKU',
            width: '90px',
            sortKey: 'sku',
            cell: (item) => item.sku,
            cellTitle: (item) => item.sku,
            cellClassName: () => 'font-mono text-muted-foreground',
        },
        brand: {
            key: 'brand',
            label: 'Marca',
            width: '100px',
            sortKey: 'brand',
            cell: (item) => item.brand || DASH,
            cellTitle: (item) => item.brand,
        },
        model: {
            key: 'model',
            label: 'Modelo',
            sortKey: 'model',
            cell: (item) => modelText(item) || DASH,
            cellTitle: modelText,
            cellClassName: () => 'font-semibold',
        },
        tier: {
            key: 'tier',
            label: 'Gama',
            width: '100px',
            cell: (item) => productTierLabel(item.tier),
            cellClassName: () => 'text-muted-foreground',
        },
        price: {
            key: 'price',
            label: 'Precio',
            width: '96px',
            numeric: true,
            sortKey: 'price',
            cell: (item) => formatMoneyCents(item.cashPriceCents),
            cellClassName: () => 'font-semibold',
        },
        stock: {
            key: 'stock',
            label: 'Stock',
            width: '64px',
            numeric: true,
            sortKey: 'stock',
            // Un servicio no lleva inventario: ni stock ni «Agotado».
            cell: (item) =>
                isService(item) ? DASH : availableOf(item) > 0 ? availableOf(item) : 'Agotado',
            cellClassName: (item) =>
                isService(item)
                    ? 'text-muted-foreground'
                    : availableOf(item) <= 0
                      ? 'font-semibold text-danger'
                      : isLowStock(item)
                        ? 'font-semibold text-warning'
                        : undefined,
        },
        promo: {
            key: 'promo',
            label: 'Promoción',
            width: '120px',
            cellOverflow: 'nowrap',
            cell: (item) =>
                hasPromo(item) ? (
                    <StatusPill tone="info" title={promoLabelOf(item) ?? 'Promo'}>
                        {promoLabelOf(item) ?? 'Promo'}
                    </StatusPill>
                ) : (
                    <span className="text-muted-foreground">{DASH}</span>
                ),
        },
    };
}

export function PosProductTable<TRow = CatalogItem>({
    rows,
    columns,
    rowKey = (row) => (row as CatalogItem).sku,
    rowClassName,
    resultCount,
    stats,
    statItems,
    stat,
    onStatChange,
    sort,
    onSortToggle,
    onActivate,
    isRowSelected,
    caption,
    emptyText,
    hint,
    isLoading,
    isError,
    onRetry,
}: {
    /** Filas ya filtradas, recortadas por la píldora y ordenadas. */
    rows: TRow[];
    columns: DataTableColumn<TRow, PosSortKey>[];
    /** Llave de la fila; por defecto el SKU. */
    rowKey?: (row: TRow) => string;
    rowClassName?: (row: TRow) => string | undefined;
    /** Cuántos resultados se anuncian; por defecto, las filas. */
    resultCount?: number;
    stats: PosStats;
    /** Qué píldoras muestra la pantalla y cómo se llaman. */
    statItems: { key: PosStat; label: string; tone?: StatsBarItem['tone'] }[];
    stat: PosStat;
    onStatChange: (next: PosStat) => void;
    sort: PosSort;
    onSortToggle: (key: PosSortKey) => void;
    onActivate: (row: TRow) => void;
    isRowSelected?: (row: TRow) => boolean;
    caption: string;
    emptyText: ReactNode;
    hint: ReactNode;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
}) {
    const count = resultCount ?? rows.length;
    if (isError) {
        return (
            <ErrorState
                className="m-3"
                title="No se pudo leer el catálogo de la tienda."
                description="Vuelve a intentarlo; si sigue igual, avísale a tu gerente."
                onRetry={onRetry}
            />
        );
    }

    return (
        <>
            <StatsBar
                ariaLabel="Recortes"
                items={statItems.map((item) => ({
                    ...item,
                    value: isLoading ? DASH : stats[item.key],
                }))}
                active={stat}
                onChange={onStatChange}
            />
            <TableToolbar>
                <span aria-live="polite">
                    <strong className="font-bold text-foreground">
                        {isLoading ? DASH : count}
                    </strong>{' '}
                    {count === 1 ? 'resultado' : 'resultados'}
                </span>
                <div className="flex-1" />
                <span className="whitespace-nowrap text-[10.5px]">{hint}</span>
            </TableToolbar>
            <DataTable
                caption={caption}
                columns={columns}
                rows={rows}
                rowKey={rowKey}
                rowClassName={rowClassName}
                sort={sort}
                onSortToggle={onSortToggle}
                onRowClick={onActivate}
                isRowSelected={isRowSelected}
                isLoading={isLoading}
                empty={<span className="text-[12.5px]">{emptyText}</span>}
            />
        </>
    );
}
